/**
 * ProposalPropertyValue — read-only rendering of ONE side of a proposal
 * property diff (the "Current" or the "Updated" column).
 *
 * This is a thin dispatcher over the canonical CRM display components, not a
 * renderer of its own: per `docs/claude/FRONTEND_PATTERNS.md` § CRM Property &
 * Association Display Components, a property must look identical in an action
 * card, a slideout and a list cell. It replaces the card-local `displayValue()`
 * stringifier, which had no notion of field type and therefore printed select
 * option *values* (`private`) instead of labels (`Private`), URLs as plain
 * text, and numbers unformatted.
 *
 *   - Stage/lifecycle keys → {@link PipelineStageValueLabel}. Stages are
 *     ASSOCIATIONS, not properties, so they have no entry in the property-type
 *     schema; resolving a stage entity_id to its label needs the per-id store
 *     lookup this component owns.
 *   - Everything else → {@link PropertyValueLabel}, driven by the property's
 *     `PropertyTypeSchema` (select labels, url links, currency, dates, …).
 *
 * Long values collapse to {@link DIFF_VALUE_MAX_LENGTH} characters. The
 * expand/collapse toggle is deliberately NOT rendered here (`hideExpandButton`)
 * — the diff row owns a single toggle so the "current" and "proposed" sides
 * always expand together and the columns stay aligned; this component reads
 * that shared state from `useDiffRowExpanded()`.
 */

import { memo } from 'react';

import { PipelineStageValueLabel } from '@/lib/crm/components/properties/labels/PipelineStageValueLabel';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

import { useDiffRowExpanded } from './diffRowExpansion';
import { isStageProperty } from './UpdateActionEditForm.helpers';

/**
 * Characters shown per diff side before the row's shared "Show more" toggle
 * appears. Roughly three lines at the diff cell's `text-sm` in a half-width
 * column — long enough to read a lede, short enough that an 18-property
 * enrichment proposal stays scannable.
 */
export const DIFF_VALUE_MAX_LENGTH = 120;

export interface ProposalPropertyValueProps {
  /** CRM property type name, e.g. `ahoy_description`. */
  propertyKey: string;
  value: unknown;
  /** Schema for `propertyKey`; absent for stage associations and unknown keys. */
  propertyType?: PropertyTypeSchema;
  orgId?: number;
}

export const ProposalPropertyValue = memo(function ProposalPropertyValue({
  propertyKey,
  value,
  propertyType,
  orgId,
}: ProposalPropertyValueProps) {
  const isExpanded = useDiffRowExpanded();

  if (isStageProperty(propertyKey) && typeof value === 'string' && value) {
    return <PipelineStageValueLabel value={value} />;
  }

  return (
    <PropertyValueLabel
      value={value}
      propertyType={propertyType}
      orgId={orgId}
      fontSize='sm'
      maxLength={isExpanded ? undefined : DIFF_VALUE_MAX_LENGTH}
      hideExpandButton
    />
  );
});

ProposalPropertyValue.displayName = 'ProposalPropertyValue';
